import React, { Fragment, useContext } from "react";
import SectionTitle from "../../../../shared/components/app/SectionTitle";
import Button from "../../../../shared/components/ui/Button";
import FormLabel from "../../../../shared/components/ui/FormLabel";
import { Box } from "../../../../shared/components/ui/chakra";
import { Options } from "../../../../shared/lib/types";
import { Step1FormData } from "../../lib/useStep1Form";
import { Step2FormData } from "../../lib/useStep2Form";
import { useCaseOptions } from "../../../../../shared/lib/useCasesOptions";
import { BusinessType, OwnedBusinessEntity } from "../../lib/types";
import { Helmet } from "react-helmet";
import { createUnifiedFile } from "../../../../../shared/lib/UnifiedFile";
import { SharedArchitecturalSoftwares } from "../../../../../shared/lib/types";

import {
  Flex,
  Heading,
  HStack,
  Stack,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import {
  normalizeNumber,
  normalizesDashedNumber,
} from "../../../../../shared/lib/numberUtils";
import ActivationModal from "./ActivationModal";
import { FlipperContext } from "../../../../../shared/lib/FlipperContext";

const Confirmation = ({
  values,
  businessTypes,
  prefectures,
  industries,
  isSubmitting,
  businessFields,
  architecturalSoftwares,
  buildingTypes,
  structureTypes,
  designedArchitecturalWorksImageUrls,
  ownedBusinessEntity,
  onSubmit,
  onClickFix,
  onClickPreview,
}: {
  values: Step1FormData & Step2FormData;
  businessTypes?: BusinessType[];
  prefectures: Options;
  industries: Options;
  isSubmitting: boolean;
  businessFields: Options;
  architecturalSoftwares: SharedArchitecturalSoftwares;
  buildingTypes: Options;
  structureTypes: Options;
  designedArchitecturalWorksImageUrls: string[][];
  ownedBusinessEntity?: OwnedBusinessEntity;
  onSubmit: () => void;
  onClickFix: () => void;
  onClickPreview: () => void;
}) => {
  const businessTypeLabel = businessTypes?.find(
    (businessType) => businessType.value === values.business_type,
  )?.label;

  const isSelectedSns =
    values.facebook_url ||
    values.instagram_url ||
    values.note_url ||
    values.pinterest_url ||
    values.threads_url ||
    values.tiktok_url ||
    values.x_url ||
    values.youtube_url;

  const logoImage = createUnifiedFile(values.logo_image);
  const activationModalDisclosure = useDisclosure();
  const flipper = useContext(FlipperContext);
  const isShowModal =
    ownedBusinessEntity &&
    ownedBusinessEntity.is_approved &&
    !ownedBusinessEntity.is_active &&
    flipper.auto_active_feature;

  return (
    <>
      <Helmet>
        <title>
          事業者情報 確認画面｜建築士のためのコミュニティA-Loop【エーループ】
        </title>
      </Helmet>
      <Heading size="lg" textAlign="center">
        事業者情報 確認画面
      </Heading>
      <Box as="section" mt={14}>
        <SectionTitle>事業者情報</SectionTitle>
        <Stack gap={7} mt={7}>
          {businessTypeLabel && (
            <Box>
              <FormLabel as="div">事業者区分</FormLabel>
              <Text>{businessTypeLabel}</Text>
            </Box>
          )}
          <Stack>
            <FormLabel as="div">事業者名</FormLabel>
            <Text>{values.name}</Text>
          </Stack>
          {values.name_kana && (
            <Stack>
              <FormLabel as="div">事業者名かな</FormLabel>
              <Text minH={6}>{values.name_kana}</Text>
            </Stack>
          )}
          {values.branch_name && (
            <Stack>
              <FormLabel as="div">支店名</FormLabel>
              <Text minH={6}>{values.branch_name}</Text>
            </Stack>
          )}
          <Stack>
            <FormLabel as="div">郵便番号</FormLabel>
            <Text>{normalizesDashedNumber(values.postal_code)}</Text>
          </Stack>
          <Stack>
            <FormLabel as="div">都道府県</FormLabel>
            <Text>
              {
                prefectures.find(
                  (prefecture) =>
                    prefecture.id.toString() ===
                    values.prefecture_id.toString(),
                )!.name
              }
            </Text>
          </Stack>
          <Stack>
            <FormLabel as="div">市区町村</FormLabel>
            <Text>{values.address1}</Text>
          </Stack>
          <Stack>
            <FormLabel as="div">番地</FormLabel>
            <Text>{normalizesDashedNumber(values.address2)}</Text>
          </Stack>
          <Stack>
            <FormLabel as="div">所在地の一部を非表示にする</FormLabel>
            <Text>{values.display_address_restricted ? "する" : "しない"}</Text>
          </Stack>
          <Stack>
            <FormLabel as="div">建物名</FormLabel>
            <Text minH={6}>{values.address3}</Text>
          </Stack>
          <Stack>
            <FormLabel as="div">電話番号</FormLabel>
            <Text>{normalizesDashedNumber(values.phone_number)}</Text>
          </Stack>
          <Stack>
            <FormLabel as="div">受発注の通知を受け取るメールアドレス</FormLabel>
            <Text>{values.email}</Text>
          </Stack>
          <Stack>
            <FormLabel as="div">主業種</FormLabel>
            <Text>
              {
                industries.find(
                  (industry) =>
                    industry.id.toString() === values.industry_id.toString(),
                )!.name
              }
            </Text>
          </Stack>
          <Stack>
            <FormLabel as="div">従業員数</FormLabel>
            <Text>{normalizeNumber(values.number_of_employees)}名</Text>
          </Stack>
          <Stack>
            <FormLabel as="div">設立月</FormLabel>
            <HStack>
              <Text>{values.establishment_year}</Text>
              <Text>年</Text>
              <Text>{values.establishment_month}</Text>
              <Text>月</Text>
            </HStack>
          </Stack>
          <Stack>
            <FormLabel as="div">資本金</FormLabel>
            <Text>{normalizeNumber(values.capital_ten_thousand_yen)}万円</Text>
          </Stack>
          <Stack>
            <FormLabel as="div">事業者ロゴ</FormLabel>
            {!logoImage.isNull ? (
              <Box as="img" w="full" minH="400px" src={logoImage.url} />
            ) : (
              <Box>未設定</Box>
            )}
          </Stack>
          <Stack>
            <FormLabel as="div">代表者氏名</FormLabel>
            <Text>{values.president_name}</Text>
          </Stack>
          {values.website_url && (
            <Stack>
              <FormLabel as="div">Webサイト</FormLabel>
              <Text minH={6}>{values.website_url}</Text>
            </Stack>
          )}
        </Stack>
      </Box>
      {isSelectedSns && (
        <Box as="section" mt={14}>
          <SectionTitle>SNS</SectionTitle>
          <Stack gap={7} mt={7}>
            {values.facebook_url && (
              <Stack>
                <FormLabel as="div">Facebook</FormLabel>
                <Text minH={6}>{values.facebook_url}</Text>
              </Stack>
            )}
            {values.instagram_url && (
              <Stack>
                <FormLabel as="div">Instagram</FormLabel>
                <Text minH={6}>{values.instagram_url}</Text>
              </Stack>
            )}
            {values.note_url && (
              <Stack>
                <FormLabel as="div">note</FormLabel>
                <Text minH={6}>{values.note_url}</Text>
              </Stack>
            )}
            {values.pinterest_url && (
              <Stack>
                <FormLabel as="div">Pinterest</FormLabel>
                <Text minH={6}>{values.pinterest_url}</Text>
              </Stack>
            )}
            {values.threads_url && (
              <Stack>
                <FormLabel as="div">Threads</FormLabel>
                <Text minH={6}>{values.threads_url}</Text>
              </Stack>
            )}
            {values.tiktok_url && (
              <Stack>
                <FormLabel as="div">TikTok</FormLabel>
                <Text minH={6}>{values.tiktok_url}</Text>
              </Stack>
            )}
            {values.x_url && (
              <Stack>
                <FormLabel as="div">X（旧Twitter）</FormLabel>
                <Text minH={6}>{values.x_url}</Text>
              </Stack>
            )}
            {values.youtube_url && (
              <Stack>
                <FormLabel as="div">YouTube</FormLabel>
                <Text minH={6}>{values.youtube_url}</Text>
              </Stack>
            )}
          </Stack>
        </Box>
      )}
      <Box as="section" mt={14}>
        <SectionTitle>受発注の利用用途</SectionTitle>
        <Box mt={7}>
          <Text>
            {useCaseOptions.find((it) => it.value === values.use_case)!.label}
          </Text>
        </Box>
      </Box>
      <Box as="section" mt={14}>
        <SectionTitle>事業者プロフィール</SectionTitle>
        <Stack gap={7} mt={7}>
          <Stack>
            <FormLabel as="div">事業者紹介</FormLabel>
            <Text whiteSpace="pre-wrap">{values.introduction}</Text>
          </Stack>
          <Stack>
            {values.used_architectural_software_ids.length > 0 && (
              <>
                <FormLabel as="div">利用している建築系ソフトウェア</FormLabel>
                <Stack gap={4}>
                  {architecturalSoftwares.map(
                    ({ category, items }) =>
                      values.used_architectural_software_ids.some((id) =>
                        items.find((it) => it.id.toString() === id),
                      ) && (
                        <Fragment key={category}>
                          <Stack>
                            <FormLabel
                              as="div"
                              w="full"
                              pb={1}
                              borderBottom="1px #C4CCCA solid"
                              fontSize="sm"
                            >
                              {category}
                            </FormLabel>
                            <Stack>
                              {values.used_architectural_software_ids.map(
                                (id) => {
                                  const item = items.find(
                                    (it) => it.id.toString() === id,
                                  );
                                  return (
                                    <Fragment key={id}>
                                      {item ? <Text>{item.name}</Text> : <></>}
                                    </Fragment>
                                  );
                                },
                              )}
                            </Stack>
                          </Stack>
                        </Fragment>
                      ),
                  )}
                </Stack>
              </>
            )}
          </Stack>
          {values.main_client && (
            <Stack>
              <FormLabel as="div">主な取引先</FormLabel>
              <Text whiteSpace="pre-wrap">{values.main_client}</Text>
            </Stack>
          )}
        </Stack>
        {["contractor", "both"].includes(values.use_case) &&
          values.contractor_info_attributes && (
            <Box mt={14}>
              <SectionTitle>受注したい業務</SectionTitle>
              <Stack gap={7} mt={7}>
                <Stack>
                  <FormLabel as="div">受注したい業種</FormLabel>
                  <Stack>
                    {values.contractor_info_attributes.desired_industry_ids.map(
                      (id) => (
                        <Text key={id}>
                          {industryById(industries, id)?.name}
                        </Text>
                      ),
                    )}
                  </Stack>
                </Stack>
                <Stack>
                  <FormLabel as="div">対応できる分野</FormLabel>
                  <Stack>
                    {values.contractor_info_attributes.supported_business_field_ids.map(
                      (id) => (
                        <Text key={id}>
                          {businessFieldById(businessFields, id)?.name}
                        </Text>
                      ),
                    )}
                  </Stack>
                </Stack>
                <Stack>
                  <FormLabel as="div">対応できる業務内容</FormLabel>
                  <Text whiteSpace="pre-wrap">
                    {
                      values.contractor_info_attributes
                        .supported_business_service_note
                    }
                  </Text>
                </Stack>
                <Stack>
                  <FormLabel as="div">対応できる建物種別</FormLabel>
                  <Stack gap={4}>
                    {values.contractor_info_attributes.contractor_info_supported_building_types_attributes.map(
                      (it) => (
                        <Stack key={it.building_type_id}>
                          <FormLabel
                            as="div"
                            w="full"
                            pb={1}
                            borderBottom="1px #C4CCCA solid"
                            fontSize="sm"
                          >
                            {
                              buildingTypeById(
                                buildingTypes,
                                it.building_type_id,
                              )?.name
                            }
                          </FormLabel>
                          <Stack>
                            {it.structure_type_ids.map((id) => (
                              <Text key={id}>
                                {structureTypeById(structureTypes, id)?.name}
                              </Text>
                            ))}
                          </Stack>
                        </Stack>
                      ),
                    )}
                  </Stack>
                </Stack>
                <Stack>
                  <FormLabel as="div">対応可能エリア</FormLabel>
                  <Text>
                    {values.contractor_info_attributes.supported_prefecture_ids
                      .map((id) => prefectureById(prefectures, id)?.name ?? "")
                      .join(", ")}
                  </Text>
                </Stack>
                <Stack>
                  <FormLabel as="div">設計実績</FormLabel>
                  <Stack>
                    {values.contractor_info_attributes.architectural_design_works_attributes.map(
                      (it, index) => (
                        <Stack
                          key={index}
                          bgColor="brand.50"
                          py={4}
                          px={5}
                          gap={4}
                        >
                          <Stack>
                            <FormLabel as="div">プロジェクト名</FormLabel>
                            <Text>{it.project_name}</Text>
                          </Stack>
                          <Stack>
                            <FormLabel as="div">建物種別</FormLabel>
                            <Text>
                              {
                                buildingTypeById(
                                  buildingTypes,
                                  it.building_type_id,
                                )?.name
                              }
                            </Text>
                          </Stack>
                          <Stack>
                            <FormLabel as="div">構造種別</FormLabel>
                            <Text>
                              {it.structure_type_ids
                                .map(
                                  (id) =>
                                    structureTypeById(structureTypes, id)?.name,
                                )
                                .join(", ")}
                            </Text>
                          </Stack>
                          <Stack>
                            <FormLabel as="div">プロジェクト詳細</FormLabel>
                            <Text whiteSpace="pre-wrap">
                              {it.project_detail}
                            </Text>
                          </Stack>
                          {it.prefecture_id && (
                            <Stack>
                              <FormLabel as="div">所在地</FormLabel>
                              <Text>
                                {
                                  prefectureById(prefectures, it.prefecture_id)
                                    ?.name
                                }
                              </Text>
                            </Stack>
                          )}
                          {it.above_ground_floors && (
                            <Stack>
                              <FormLabel as="div">地上</FormLabel>
                              <Text>
                                {normalizeNumber(it.above_ground_floors)}階
                              </Text>
                            </Stack>
                          )}
                          {it.underground_floors && (
                            <Stack>
                              <FormLabel as="div">地下</FormLabel>
                              <Text>
                                {normalizeNumber(it.underground_floors)}階
                              </Text>
                            </Stack>
                          )}
                          {it.total_floor_area && (
                            <Stack>
                              <FormLabel as="div">延床面積</FormLabel>
                              <Text>
                                {normalizeNumber(it.total_floor_area)}㎡
                              </Text>
                            </Stack>
                          )}
                          {it.completion_year && (
                            <Stack>
                              <FormLabel as="div">竣工年</FormLabel>
                              <Text>
                                {normalizeNumber(it.completion_year)}年
                              </Text>
                            </Stack>
                          )}
                          {designedArchitecturalWorksImageUrls[index].length >
                            0 && (
                            <Stack>
                              <FormLabel as="div">建物写真</FormLabel>
                              {designedArchitecturalWorksImageUrls[index]}
                              <Stack>
                                {designedArchitecturalWorksImageUrls[index].map(
                                  (file, index) => (
                                    <Box
                                      as="img"
                                      src={file}
                                      key={index}
                                      objectFit="cover"
                                      aspectRatio={3 / 2}
                                    />
                                  ),
                                )}
                              </Stack>
                            </Stack>
                          )}
                        </Stack>
                      ),
                    )}
                  </Stack>
                </Stack>
              </Stack>
            </Box>
          )}
        {["client", "both"].includes(values.use_case) &&
          values.client_info_attributes && (
            <Box mt={14}>
              <SectionTitle>発注したい業務</SectionTitle>
              <Stack gap={7} mt={7}>
                <Stack>
                  <FormLabel as="div">発注したい業務</FormLabel>
                  <Stack>
                    {values.client_info_attributes.desired_industry_ids.map(
                      (id) => (
                        <Text key={id}>
                          {industryById(industries, id)?.name}
                        </Text>
                      ),
                    )}
                  </Stack>
                </Stack>
                <Stack>
                  <FormLabel as="div">発注したい分野</FormLabel>
                  <Stack>
                    {values.client_info_attributes.desired_business_field_ids.map(
                      (id) => (
                        <Text key={id}>
                          {businessFieldById(businessFields, id)?.name}
                        </Text>
                      ),
                    )}
                  </Stack>
                </Stack>
                <Stack>
                  <FormLabel as="div">発注したい業務内容</FormLabel>
                  <Text whiteSpace="pre-wrap">
                    {
                      values.client_info_attributes
                        .desired_business_service_note
                    }
                  </Text>
                </Stack>
              </Stack>
            </Box>
          )}
      </Box>

      <Box
        mt={8}
        paddingBottom={3}
        borderBottomColor="brand.500"
        borderBottomWidth="2px"
      />
      <Button onClick={onClickPreview} w="full" mt={10} variant="outline">
        実際の表示をプレビューする
      </Button>
      <Flex justify="center" mt={5} gap={3}>
        <Button
          type="button"
          isLoading={false}
          isDisabled={isSubmitting}
          w="full"
          variant="outline"
          onClick={onClickFix}
          wordBreak="keep-all"
          overflowWrap="anywhere"
        >
          入力内容を&#8203;修正
        </Button>
        <Button
          type="button"
          onClick={isShowModal ? activationModalDisclosure.onOpen : onSubmit}
          isLoading={isSubmitting}
          w="full"
        >
          上記内容で申請
        </Button>
        {isShowModal && (
          <ActivationModal
            disclosure={activationModalDisclosure}
            onSubmit={onSubmit}
            isSubmitting={isSubmitting}
          />
        )}
      </Flex>
    </>
  );
};

const prefectureById = (prefectures: Options, id: string) =>
  prefectures.find((it) => it.id.toString() === id);

const structureTypeById = (structureTypes: Options, id: string) =>
  structureTypes.find((it) => it.id.toString() === id);

const businessFieldById = (businessFields: Options, id: string) =>
  businessFields.find((it) => it.id.toString() === id);

const buildingTypeById = (buildingTypes: Options, id: string) =>
  buildingTypes.find((it) => it.id.toString() === id);

const industryById = (industries: Options, id: string) =>
  industries.find((it) => it.id.toString() === id);

export default Confirmation;
